import { useEffect, useId, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getAdminBillingTenants,
  getTenantAIUsage,
  patchAdminBillingTenant,
  type TenantBillingRow,
  type TenantAIUsage,
  type UsageWindow,
} from '../../packages/api-client/admin-billing-api';
import { PLANS, type PlanKey, type BillingStatus } from '../../config/plans';
import { usePageTitle } from '../../hooks/usePageTitle';
import { PageHeader, Table, StateView, Dialog, type Column } from '../../packages/ui';
import './platformOps.css';

const STATUSES: { value: BillingStatus; label: string; tone: string }[] = [
  { value: 'paid', label: 'Paid', tone: 'ops-pill ops-pill--ok' },
  { value: 'trial', label: 'Trial', tone: 'ops-pill' },
  { value: 'unpaid', label: 'Unpaid', tone: 'ops-pill ops-pill--danger' },
  { value: 'cancelled', label: 'Cancelled', tone: 'ops-pill ops-pill--warn' },
];
const CYCLES = [{ value: 'monthly', label: 'Monthly' }, { value: 'annual', label: 'Annual' }] as const;
const WINDOWS: { value: UsageWindow; label: string }[] = [
  { value: '7d', label: 'Last 7 days' }, { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' }, { value: 'all', label: 'All time' },
];

export const NOT_STRIPE = 'Changes the record only; not a Stripe change';

const planLabel = (p: string) => PLANS.find(x => x.key === p)?.label ?? p;
const statusMeta = (s: string) => STATUSES.find(x => x.value === s) ?? { value: s, label: s, tone: 'ops-pill' };
const cycleLabel = (c: string) => (c ? CYCLES.find(x => x.value === c)?.label ?? c : '—');
const windowLabel = (w: UsageWindow) => WINDOWS.find(x => x.value === w)?.label ?? w;
/** 612000 → "612K" */
const compact = (n: number) => Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
/** Absent or unparseable → em dash, never "Invalid Date". */
const when = (iso?: string | null, dateOnly = false) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return dateOnly ? d.toLocaleDateString() : d.toLocaleString();
};

type Row = TenantBillingRow & Record<string, unknown>;
// A refresh (e.g. a new usage window) keeps the previous rows on screen, so the
// selected tenant's detail stays mounted and an unsaved draft survives.
type Load = { kind: 'loading' } | { kind: 'error'; rows?: Row[] } | { kind: 'ok'; rows: Row[]; refreshing?: boolean };

export function SuperAdminBillingPage() {
  usePageTitle('Billing & AI usage');
  const [params, setParams] = useSearchParams();
  const usageWindow = (WINDOWS.some(w => w.value === params.get('window')) ? params.get('window') : '30d') as UsageWindow;
  const tenantId = params.get('tenant') ?? '';
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    let live = true;
    setLoad(prev => (prev.kind === 'ok' ? { ...prev, refreshing: true } : { kind: 'loading' }));
    getAdminBillingTenants(usageWindow)
      .then(rows => { if (live) setLoad({ kind: 'ok', rows: (rows ?? []) as Row[] }); })
      .catch(() => { if (live) setLoad(prev => ({ kind: 'error', rows: prev.kind === 'ok' ? prev.rows : undefined })); });
    return () => { live = false; };
  }, [usageWindow, reload]);

  const setParam = (key: string, value: string) => setParams(prev => {
    const next = new URLSearchParams(prev);
    if (value) next.set(key, value); else next.delete(key);
    return next;
  }, { replace: true });

  const rows = (load.kind === 'loading' ? undefined : load.rows) ?? [];
  const selected = rows.find(r => r.id === tenantId) ?? null;
  const filtered = rows.filter(r => (!planFilter || r.plan === planFilter) && (!statusFilter || r.billing_status === statusFilter));
  const count = (s: BillingStatus) => rows.filter(r => r.billing_status === s).length;
  const onSaved = (id: string, patch: Partial<TenantBillingRow>) => setLoad(prev => prev.kind === 'ok'
    ? { kind: 'ok', rows: prev.rows.map(r => (r.id === id ? { ...r, ...patch } : r)) }
    : prev);

  const columns: Column<Row>[] = [
    { key: 'name', header: 'Workspace', rowHeader: true, render: r => <>{r.name}<span className="ops-sub">{r.slug}{!r.is_active && ' · inactive'}</span></> },
    { key: 'plan', header: 'Plan / cycle', render: r => <>{planLabel(r.plan)}<span className="ops-sub">{cycleLabel(r.billing_cycle)}</span></> },
    { key: 'billing_status', header: 'Billing status', render: r => <span className={statusMeta(r.billing_status).tone}>{statusMeta(r.billing_status).label}</span> },
    { key: 'user_count', header: 'Users', render: r => <span className="ops-num">{r.user_count ?? '—'}</span> },
    { key: 'project_count', header: 'Projects', render: r => <span className="ops-num">{r.project_count ?? '—'}</span> },
    { key: 'ai_calls', header: 'AI calls', render: r => <span className="ops-num">{r.ai_calls.toLocaleString()}</span> },
    { key: 'ai_tokens', header: 'Tokens', render: r => <span className="ops-num">{compact(r.ai_tokens)}</span> },
    { key: 'created_at', header: 'Created', render: r => when(r.created_at, true) },
    {
      key: 'paid', header: 'Paid / trial ends',
      render: r => (r.billing_status === 'trial' && r.trial_ends_at
        ? `Trial ends ${when(r.trial_ends_at, true)}`
        : r.paid_at ? `Paid ${when(r.paid_at, true)}` : '—'),
    },
    {
      key: 'inspect', header: 'Details',
      render: r => <button type="button" className="btn btn-ghost btn-sm" aria-label={`Inspect ${r.name}`} onClick={() => setParam('tenant', r.id)}>Inspect</button>,
    },
  ];

  return (
    <div className="ops-page">
      <PageHeader
        breadcrumb={[{ label: 'Platform' }, { label: 'Billing & AI usage' }]}
        title="Billing & AI usage"
        subtitle="Plan, billing status and cycle recorded for each tenant, with KaziAtlas-recorded AI calls. Payments, invoices and subscriptions stay in Stripe."
      />

      <div className="ops-toolbar">
        <label className="ops-field">Tenant
          <select className="form-input" value={selected ? tenantId : ''} onChange={e => setParam('tenant', e.target.value)} disabled={rows.length === 0}>
            <option value="">Choose a tenant…</option>
            {rows.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
        <label className="ops-field">AI usage window
          <select className="form-input" value={usageWindow} onChange={e => setParam('window', e.target.value === '30d' ? '' : e.target.value)}>
            {WINDOWS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </label>
      </div>

      {load.kind === 'loading' && <StateView state="loading" label="Loading tenant billing" lines={6} />}
      {load.kind === 'error' && !load.rows && (
        <StateView state="error" title="Could not load tenants"
          message="Tenant billing and usage did not load. Nothing on this page is current."
          onRetry={() => setReload(n => n + 1)} />
      )}
      {load.kind === 'error' && load.rows && (
        <div role="alert" className="ops-callout">
          Could not refresh tenants for {windowLabel(usageWindow).toLowerCase()}; the figures below are from the previous window.{' '}
          <button type="button" className="btn btn-secondary btn-md" onClick={() => setReload(n => n + 1)}>Retry</button>
        </div>
      )}
      {load.kind === 'ok' && load.refreshing && <p role="status" className="ops-scope">Refreshing for {windowLabel(usageWindow).toLowerCase()}…</p>}
      {load.kind === 'ok' && rows.length === 0 && <StateView state="empty" title="No tenants yet" message="Tenants appear here once a workspace is created." />}

      {load.kind === 'ok' && !load.refreshing && tenantId && !selected && (
        <StateView state="empty" title="That tenant isn't in the registry"
          message="It may have been deleted. Choose another tenant."
          action={<button type="button" className="btn btn-secondary btn-md" onClick={() => setParam('tenant', '')}>Clear selection</button>} />
      )}
      {selected && (
        <TenantDetail key={selected.id} row={selected} usageWindow={usageWindow} onSaved={onSaved} />
      )}

      {rows.length > 0 && (
        <section className="ops-section" aria-labelledby="billing-all">
          <h2 id="billing-all" className="ops-scope">All tenants · {rows.length} total · {count('paid')} paid · {count('trial')} trial · {count('unpaid')} unpaid · {count('cancelled')} cancelled</h2>
          <div className="ops-toolbar">
            <label className="ops-field">Plan
              <select className="form-input" value={planFilter} onChange={e => setPlanFilter(e.target.value)}>
                <option value="">All plans</option>
                {PLANS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </label>
            <label className="ops-field">Billing status
              <select className="form-input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
          </div>
          {filtered.length === 0
            ? <StateView state="filtered-empty" title="No tenants match these filters" onClearFilters={() => { setPlanFilter(''); setStatusFilter(''); }} />
            : <Table<Row> caption={`Tenants · AI usage ${windowLabel(usageWindow).toLowerCase()}`} hideCaption density="compact" columns={columns} data={filtered} rowKey={r => r.id} />}
        </section>
      )}
    </div>
  );
}

type AI = { kind: 'loading' } | { kind: 'error' } | { kind: 'ok'; data: TenantAIUsage };
type Draft = { plan: PlanKey; billing_status: BillingStatus; billing_cycle: TenantBillingRow['billing_cycle'] };

/** Keyed by tenant id, so switching tenants discards every piece of the previous tenant's state. */
function TenantDetail({ row, usageWindow, onSaved }: {
  row: TenantBillingRow; usageWindow: UsageWindow; onSaved: (id: string, patch: Partial<TenantBillingRow>) => void;
}) {
  const [ai, setAi] = useState<AI>({ kind: 'loading' });
  const [aiReload, setAiReload] = useState(0);
  const [draft, setDraft] = useState<Draft>({ plan: row.plan, billing_status: row.billing_status, billing_cycle: row.billing_cycle });
  const [confirming, setConfirming] = useState(false);
  const [save, setSave] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const noteId = useId();

  useEffect(() => {
    let live = true;
    setAi({ kind: 'loading' });
    getTenantAIUsage(row.id, usageWindow)
      .then(data => { if (live) setAi({ kind: 'ok', data }); })
      .catch(() => { if (live) setAi({ kind: 'error' }); });
    return () => { live = false; };
  }, [row.id, usageWindow, aiReload]);

  const changes = (Object.keys(draft) as (keyof Draft)[]).filter(k => draft[k] !== row[k]);
  const describe = (k: keyof Draft, v: string) => (k === 'plan' ? planLabel(v) : k === 'billing_status' ? statusMeta(v).label : cycleLabel(v));
  const FIELD: Record<keyof Draft, string> = { plan: 'Plan', billing_status: 'Billing status', billing_cycle: 'Billing cycle' };

  async function confirm() {
    const patch = Object.fromEntries(changes.map(k => [k, draft[k]])) as Parameters<typeof patchAdminBillingTenant>[1];
    setSave('saving');
    try {
      await patchAdminBillingTenant(row.id, patch);
      onSaved(row.id, patch);
      setSave('saved');
      setConfirming(false);
    } catch {
      setSave('error'); // dialog and draft stay so the operator can retry
    }
  }

  return (
    <section className="ops-panel" aria-labelledby="tenant-detail">
      <h2 id="tenant-detail">{row.name}</h2>
      <div className="ops-metrics">
        <div className="ops-metric"><strong>{row.user_count ?? '—'}</strong><span>Users</span></div>
        <div className="ops-metric"><strong>{row.project_count ?? '—'}</strong><span>Projects</span></div>
        <div className="ops-metric"><strong>{row.ai_calls.toLocaleString()}</strong><span>AI calls · {windowLabel(usageWindow).toLowerCase()}</span></div>
        <div className="ops-metric"><strong>{compact(row.ai_tokens)}</strong><span>Tokens (input + output)</span></div>
      </div>

      <dl className="ops-detail">
        <dt>Plan</dt><dd>{planLabel(row.plan)}</dd>
        <dt>Billing status</dt><dd>{statusMeta(row.billing_status).label}</dd>
        <dt>Billing cycle</dt><dd>{cycleLabel(row.billing_cycle)}</dd>
        <dt>Paid at</dt><dd>{when(row.paid_at, true)}</dd>
        <dt>Trial ends</dt><dd>{when(row.trial_ends_at, true)}</dd>
        <dt>Created</dt><dd>{when(row.created_at, true)}</dd>
        <dt>Last AI use</dt><dd>{when(row.ai_last_used)}</dd>
        <dt>Tenant ID</dt><dd className="ops-mono">{row.id}</dd>
      </dl>

      <form className="ops-section" aria-labelledby="billing-meta" onSubmit={e => { e.preventDefault(); setSave('idle'); setConfirming(true); }}>
        <h3 id="billing-meta">Billing metadata</h3>
        <p id={noteId} className="ops-callout"><strong>{NOT_STRIPE}.</strong> Saving updates KaziAtlas's record of this tenant. It does not charge a card, issue an invoice or change a Stripe subscription.</p>
        <div className="ops-split">
          <label className="ops-field">Plan
            <select className="form-input" aria-describedby={noteId} value={draft.plan} onChange={e => setDraft(d => ({ ...d, plan: e.target.value as PlanKey }))}>
              {PLANS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </label>
          <label className="ops-field">Billing status
            <select className="form-input" aria-describedby={noteId} value={draft.billing_status} onChange={e => setDraft(d => ({ ...d, billing_status: e.target.value as BillingStatus }))}>
              {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <label className="ops-field">Billing cycle
            <select className="form-input" aria-describedby={noteId} value={draft.billing_cycle} onChange={e => setDraft(d => ({ ...d, billing_cycle: e.target.value as Draft['billing_cycle'] }))}>
              {!row.billing_cycle && <option value="" disabled>Not set</option>}
              {CYCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
        </div>
        <div className="ops-toolbar">
          <button type="submit" className="btn btn-primary btn-md" disabled={changes.length === 0}>Review change</button>
          {save === 'saved' && changes.length === 0 && <span role="status" className="ops-inline-ok">Record updated. No Stripe change was made.</span>}
        </div>
      </form>

      <Dialog open={confirming} onClose={() => { if (save !== 'saving') setConfirming(false); }} closeOnBackdrop={false}
        title="Confirm billing metadata change"
        footer={<>
          <button type="button" className="btn btn-secondary btn-md" onClick={() => setConfirming(false)} disabled={save === 'saving'}>Cancel</button>
          <button type="button" className="btn btn-primary btn-md" onClick={confirm} disabled={save === 'saving'} aria-busy={save === 'saving' || undefined}>
            {save === 'saving' ? 'Saving…' : 'Confirm change'}
          </button>
        </>}>
        <p className="ops-callout"><strong>{NOT_STRIPE}.</strong> No payment, invoice or subscription is created or changed.</p>
        <dl className="ops-detail">
          <dt>Tenant</dt><dd>{row.name}</dd>
          {changes.map(k => <FieldChange key={k} label={FIELD[k]} from={describe(k, row[k])} to={describe(k, draft[k])} />)}
        </dl>
        {save === 'error' && <p role="alert" className="ops-inline-error">Not saved. Nothing changed; try again.</p>}
      </Dialog>

      <section className="ops-section" aria-labelledby="ai-users">
        <h3 id="ai-users">Per-user AI usage · {windowLabel(usageWindow).toLowerCase()}</h3>
        {ai.kind === 'loading' && <StateView state="loading" label="Loading AI usage" lines={3} />}
        {ai.kind === 'error' && <StateView state="error" title="AI usage couldn't be loaded" message="Per-user usage and recent events did not load." onRetry={() => setAiReload(n => n + 1)} />}
        {ai.kind === 'ok' && (ai.data.users?.length
          ? <Table caption="Per-user AI usage" hideCaption density="compact" rowKey={u => String(u.user_id)}
              data={ai.data.users as (TenantAIUsage['users'][number] & Record<string, unknown>)[]}
              columns={[
                { key: 'email', header: 'User', rowHeader: true, render: u => u.email || 'Unknown user' },
                { key: 'calls', header: 'Calls', render: u => <span className="ops-num">{u.calls.toLocaleString()}</span> },
                { key: 'tokens', header: 'Tokens', render: u => <span className="ops-num">{compact(u.tokens)}</span> },
                { key: 'last_used', header: 'Last used', render: u => when(u.last_used) },
              ]} />
          : <StateView state="empty" title="No AI usage in this window" />)}
      </section>

      <section className="ops-section" aria-labelledby="ai-recent">
        <h3 id="ai-recent">Recent AI events</h3>
        <p className="ops-scope">The latest 50 calls for this tenant, whatever the window.</p>
        {ai.kind === 'ok' && (ai.data.recent?.length
          ? <Table caption="Latest 50 AI calls" hideCaption density="compact"
              data={ai.data.recent as (TenantAIUsage['recent'][number] & Record<string, unknown>)[]}
              columns={[
                { key: 'created_at', header: 'Time', render: e => when(e.created_at) },
                { key: 'email', header: 'User', render: e => e.email || 'Unknown user' },
                { key: 'surface', header: 'Surface' },
                { key: 'tokens', header: 'Tokens', render: e => <span className="ops-num">{compact(e.input_tokens + e.output_tokens)}</span> },
                { key: 'ok', header: 'Result', render: e => (e.ok ? 'Succeeded' : 'Failed') },
              ]} />
          : <StateView state="empty" title="No recent AI events" />)}
      </section>
    </section>
  );
}

function FieldChange({ label, from, to }: { label: string; from: string; to: string }) {
  return <><dt>{label}</dt><dd>{from} → {to}</dd></>;
}
